import Avatar from '@atlaskit/avatar';
import NewButton from '@atlaskit/button/new';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { LinkClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { deleteReview } from '@atlassian/mpac-api-client';
import { BodyNormal, breakpointValues, H4, Ratings, useResponsiveBreakpoint } from '@atlassian/mpac-primitives';
import { usePartnerPermissions } from '@atlassian/mpac-ui';
import { log } from '@atlassian/mpac-utils';
import React, { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { LazySuspense } from 'react-loosely-lazy';
import { ANALYTICS_SUBJECT_ID_REVIEW_PROFILE_CLICKABLE_LINK } from '../../../../../../analytics/Events';
import { TEST_ID } from '../../../../../../constants';
import { FlagReview } from '../FlagReview';
import { VendorResponse } from '../VendorResponse';
import { LazyVendorResponseEditor, VendorResponseEditorSkeleton } from '../VendorResponseEditor';
import { VoteReview } from '../VoteReview';
import { ActionsContainer, ActionsContainerElement, Container, Content, DeleteModalButtons, Header, HeaderRight, LoadingButtonStyled, RatingsContainer, ResponseContainer, SubtleLinkStyled, SubtleText } from './UserReview.styles';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const getFormattedDate = date => {
  const [month, day, year] = date.split(' ');
  return "".concat(month, " ").concat(day.replace(/\D/g, ''), ", ").concat(year);
};
/* eslint-disable complexity */
// eslint-disable-next-line max-lines-per-function
export const UserReview = _ref => {
  let {
    index,
    isOwnReview = false,
    onEditReview,
    author,
    ratings,
    date,
    review,
    helpfulVotes,
    totalVotes,
    voteUpEndpoint,
    voteDownEndpoint,
    selfEndpoint,
    user,
    appKey,
    flagEndpoint,
    mpacBaseUrl,
    loginHref,
    vendorDetails,
    filterOutDeletedReview,
    filterOutDeletedVendorResponse,
    id
  } = _ref;
  const {
    value: screenSize
  } = useResponsiveBreakpoint();
  const isMobile = screenSize <= breakpointValues.xs;
  const {
    isPending: isDeletingInProgress,
    isFulfilled: isDeleteSuccessful,
    isRejected: isDeleteRejected,
    error: deleteReviewError,
    run: runDelete
  } = useAsync({
    deferFn: deleteReview
  });
  const [showEditor, setShowEditor] = useState(false);
  const [postedVendorResponse, setPostedVendorResponse] = useState('');
  const response = postedVendorResponse || vendorDetails.response;
  const {
    picture,
    name,
    isReviewAuthor,
    profileUrl
  } = author;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showDeleteBanner, setShowDeleteBanner] = useState(null);
  useEffect(() => {
    if (isDeleteRejected) {
      setShowDeleteBanner('failure');
      log.error('ReviewsTab', 'failed to delete a Review', {
        error: deleteReviewError
      });
    }
  }, [isDeleteRejected]);
  const permissions = usePartnerPermissions();
  const fireAnalyticsEvent = useAnalyticsEvents();
  const handlePostedVendorResponse = value => {
    setPostedVendorResponse(value);
    setShowEditor(false);
  };
  const handleEditVendorResponse = () => {
    setShowEditor(true);
  };
  const handleAnalytics = () => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_REVIEW_PROFILE_CLICKABLE_LINK,
      attributes: {
        label: 'User Profile Clicked',
        url: profileUrl
      }
    });
  };
  useEffect(() => {
    if (isDeleteSuccessful && selfEndpoint) {
      setShowDeleteBanner('success');
      filterOutDeletedReview === null || filterOutDeletedReview === void 0 ? void 0 : filterOutDeletedReview(selfEndpoint);
    }
  }, [isDeleteSuccessful]);
  const handleShowVendorResponseEditor = () => {
    setShowEditor(!showEditor);
  };
  const handleDeleteReview = endpoint => {
    runDelete("".concat(mpacBaseUrl).concat(endpoint));
  };
  const handleDeletedVendorResponse = () => {
    setPostedVendorResponse('');
    filterOutDeletedVendorResponse === null || filterOutDeletedVendorResponse === void 0 ? void 0 : filterOutDeletedVendorResponse(index);
  };
  const preloadVendorResponseEditor = () => {
    var _LazyVendorResponseEd;
    LazyVendorResponseEditor === null || LazyVendorResponseEditor === void 0 ? void 0 : (_LazyVendorResponseEd = LazyVendorResponseEditor.preload) === null || _LazyVendorResponseEd === void 0 ? void 0 : _LazyVendorResponseEd.call(LazyVendorResponseEditor);
  };
  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const resetDeleteBanner = () => setShowDeleteBanner(null);
  return /*#__PURE__*/_jsxs(Container, {
    "data-testid": TEST_ID.REVIEWS_TAB_USER_REVIEW,
    children: [isMobile ? /*#__PURE__*/_jsxs(Header, {
      children: [/*#__PURE__*/_jsx(Avatar, {
        src: picture,
        size: "medium",
        name: name
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(SubtleLinkStyled, {
          size: "small",
          onClick: handleAnalytics,
          href: profileUrl,
          children: name
        }), /*#__PURE__*/_jsx(Ratings, {
          testId: "ratings",
          ratings: ratings,
          showNumberRatings: false
        })]
      }), flagEndpoint && /*#__PURE__*/_jsx(HeaderRight, {
        children: /*#__PURE__*/_jsx(FlagReview, {
          isPopup: true,
          mpacBaseUrl: mpacBaseUrl,
          flagEndpoint: flagEndpoint
        })
      })]
    }) : /*#__PURE__*/_jsx(Avatar, {
      src: picture,
      size: "medium",
      name: name
    }), /*#__PURE__*/_jsxs(Content, {
      children: [isMobile ? /*#__PURE__*/_jsxs(SubtleText, {
        children: ["Posted on ", getFormattedDate(date)]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SubtleLinkStyled, {
          onClick: handleAnalytics,
          size: "small",
          href: profileUrl,
          children: name
        }), /*#__PURE__*/_jsxs(RatingsContainer, {
          children: [/*#__PURE__*/_jsx(Ratings, {
            testId: "ratings",
            ratings: ratings,
            showNumberRatings: false
          }), /*#__PURE__*/_jsxs(SubtleText, {
            children: ["Posted on ", getFormattedDate(date)]
          })]
        })]
      }), /*#__PURE__*/_jsx(BodyNormal, {
        children: review
      }), /*#__PURE__*/_jsxs(ActionsContainer, {
        children: [/*#__PURE__*/_jsx(VoteReview, {
          isOwnReview: isOwnReview,
          mpacBaseUrl: mpacBaseUrl,
          voteUpEndpoint: voteUpEndpoint,
          voteDownEndpoint: voteDownEndpoint,
          totalVotes: totalVotes,
          helpfulVotes: helpfulVotes,
          isLoggedIn: user.isLoggedIn,
          loginHref: loginHref,
          reviewId: id,
          appKey: appKey
        }), isOwnReview && /*#__PURE__*/_jsx(ActionsContainerElement, {
          children: /*#__PURE__*/_jsx(LoadingButtonStyled, {
            appearance: "subtle-link",
            onClick: onEditReview,
            spacing: "none",
            iconBefore: /*#__PURE__*/_jsx(EditIcon, {
              label: "Edit"
            }),
            testId: TEST_ID.REVIEWS_TAB_WRITE_EDIT_REVIEW_BUTTON,
            children: "Edit"
          })
        }), flagEndpoint && !isMobile && !isOwnReview && /*#__PURE__*/_jsx(ActionsContainerElement, {
          children: /*#__PURE__*/_jsx(FlagReview, {
            mpacBaseUrl: mpacBaseUrl,
            flagEndpoint: flagEndpoint
          })
        }), selfEndpoint && (user.isMpacAdmin || isReviewAuthor) ? /*#__PURE__*/_jsxs(ActionsContainerElement, {
          children: [/*#__PURE__*/_jsx(FlagGroup, {
            children: showDeleteBanner === 'failure' && /*#__PURE__*/_jsx(AutoDismissFlag, {
              id: "delete-review-not-submitted",
              icon: /*#__PURE__*/_jsx(CrossCircleIcon, {
                label: "Failure",
                color: "var(--ds-icon-danger, #C9372C)"
              }),
              title: "Feedback not submitted",
              description: "We\u2019re are unable to load your response. Refresh the page or try again in some time.",
              onDismissed: resetDeleteBanner,
              actions: [{
                content: 'Got it',
                onClick: resetDeleteBanner
              }]
            }, 1)
          }), /*#__PURE__*/_jsx(LoadingButtonStyled, {
            appearance: "subtle-link",
            isLoading: isDeletingInProgress,
            spacing: "none",
            iconBefore: /*#__PURE__*/_jsx(DeleteIcon, {
              label: "delete"
            }),
            onClick: () => setIsDeleteModalOpen(true),
            "aria-label": "Delete User Review",
            testId: TEST_ID.REVIEWS_TAB_DELETE_REVIEW,
            children: "Delete"
          }), /*#__PURE__*/_jsx(ModalTransition, {
            children: isDeleteModalOpen && /*#__PURE__*/_jsxs(Modal, {
              label: "Delete confirmation",
              onClose: closeDeleteModal,
              children: [/*#__PURE__*/_jsx(ModalHeader, {
                children: /*#__PURE__*/_jsx(ModalTitle, {
                  children: /*#__PURE__*/_jsx(H4, {
                    as: "h1",
                    children: "Delete review"
                  })
                })
              }), /*#__PURE__*/_jsx(ModalBody, {
                children: /*#__PURE__*/_jsx(BodyNormal, {
                  children: "Are you sure you want to delete your review? Once deleted, it can\u2019t be retrieved and the review will be lost."
                })
              }), /*#__PURE__*/_jsx(ModalFooter, {
                children: /*#__PURE__*/_jsxs(DeleteModalButtons, {
                  children: [/*#__PURE__*/_jsx(NewButton, {
                    appearance: "subtle",
                    onClick: closeDeleteModal,
                    children: "Cancel"
                  }), /*#__PURE__*/_jsx(NewButton, {
                    "aria-label": "Confirm Delete Review",
                    appearance: "primary",
                    onClick: () => {
                      handleDeleteReview(selfEndpoint);
                      closeDeleteModal();
                    },
                    children: "Confirm"
                  })]
                })
              })]
            })
          })]
        }) : null, !response && permissions && !isOwnReview && /*#__PURE__*/_jsx(ActionsContainerElement, {
          children: /*#__PURE__*/_jsx(LoadingButtonStyled, {
            testId: TEST_ID.REVIEWS_TAB_RESPOND_REVIEW,
            onClick: handleShowVendorResponseEditor,
            onMouseEnter: preloadVendorResponseEditor,
            "aria-label": "Respond to Review",
            appearance: "subtle-link",
            spacing: "none",
            children: "Reply"
          })
        })]
      }), showEditor || response ? /*#__PURE__*/_jsxs(ResponseContainer, {
        children: [showEditor && /*#__PURE__*/_jsx(LazySuspense, {
          fallback: /*#__PURE__*/_jsx(VendorResponseEditorSkeleton, {}),
          children: /*#__PURE__*/_jsx(LazyVendorResponseEditor, {
            logo: vendorDetails.logo,
            closeVendorResponseEditor: handleShowVendorResponseEditor,
            vendorResponseEndpoint: vendorDetails.responseEndpoint,
            onVendorResponseSubmit: handlePostedVendorResponse,
            response: response,
            reviewId: id
          })
        }), !showEditor && response ? /*#__PURE__*/_jsx(VendorResponse, {
          name: vendorDetails.name,
          logo: vendorDetails.logo,
          response: response,
          vendorResponseEndpoint: vendorDetails.responseEndpoint,
          mpacBaseUrl: mpacBaseUrl,
          onEditVendorResponse: handleEditVendorResponse,
          permissions: permissions,
          preloadVendorResponseEditor: preloadVendorResponseEditor,
          handleDeletedVendorResponse: handleDeletedVendorResponse
        }) : null]
      }) : null]
    })]
  });
};